// web/src/pages/admin/QuoteTemplatesPage.tsx
//
// Q-7 — Admin CRUD for quote templates (`/api/quote-templates`).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  quoteTemplatesApi,
  type QuoteTemplateDto,
  type CreateQuoteTemplateRequest,
  type UpdateQuoteTemplateRequest,
} from "@/lib/quote-admin";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { RowActions } from "@/components/row-actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Drawer form state
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  description: string;
  bodyText: string;
  isDefault: boolean;
}

function emptyForm(): FormState {
  return { name: "", description: "", bodyText: "", isDefault: false };
}

function dtoToForm(dto: QuoteTemplateDto): FormState {
  return {
    name: dto.name,
    description: dto.description ?? "",
    bodyText: dto.bodyText ?? "",
    isDefault: dto.isDefault,
  };
}

// ---------------------------------------------------------------------------
// QuoteTemplatesPage
// ---------------------------------------------------------------------------

export function QuoteTemplatesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<QuoteTemplateDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["quote-templates"],
    queryFn: () => quoteTemplatesApi.list(),
  });

  const visibleTemplates = templates.filter((t) => !t.deleted);

  function openNew() {
    setEditing(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  }

  function openEdit(t: QuoteTemplateDto) {
    setEditing(t);
    setForm(dtoToForm(t));
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const body: UpdateQuoteTemplateRequest = {
          name: form.name,
          description: form.description || null,
          bodyText: form.bodyText || null,
          isDefault: form.isDefault,
        };
        await quoteTemplatesApi.update(editing.quoteTemplateId, body);
      } else {
        const body: CreateQuoteTemplateRequest = {
          name: form.name,
          description: form.description || null,
          bodyText: form.bodyText || null,
          isDefault: form.isDefault,
        };
        await quoteTemplatesApi.create(body);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-templates"] });
      toast.success(editing ? "Template updated." : "Template created.");
      closeDrawer();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const del = useMutation({
    mutationFn: (id: number) => quoteTemplatesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-templates"] });
      toast.success("Template deleted.");
    },
    onError: (err: Error) => {
      const msg = err.message.toLowerCase().includes("409") || err.message.toLowerCase().includes("in use")
        ? "Template is in use by one or more quotes."
        : err.message;
      toast.error(`Delete failed: ${msg}`);
    },
  });

  async function handleDelete(t: QuoteTemplateDto) {
    const ok = await confirm({
      title: "Delete template?",
      body: t.name,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(t.quoteTemplateId);
  }

  const cols: DataGridColumn<QuoteTemplateDto>[] = [
    { id: "name", header: "Name", accessor: (t) => t.name },
    { id: "description", header: "Description", accessor: (t) => t.description ?? "—" },
    {
      id: "default",
      header: "Default",
      accessor: (t) => (t.isDefault ? "Yes" : "No"),
    },
    {
      id: "created",
      header: "Created",
      accessor: (t) =>
        t.createdOn ? new Date(t.createdOn).toLocaleDateString("en-AU") : "—",
    },
    {
      id: "actions",
      header: "",
      accessor: (t) => (
        <div className="flex justify-end pr-2">
          <RowActions
            onEdit={() => openEdit(t)}
            onDelete={() => void handleDelete(t)}
            deleteDisabled={del.isPending}
            deleteTooltip="Delete Template"
          />
        </div>
      ),
      align: "right",
    },
  ];

  return (
    <>
      <ListPage backTo="/admin"
        title="Quote Templates"
        primaryAction={
          <IntentButton onClick={openNew} aria-label="Add quote template">
            <Plus className="mr-2 h-4 w-4" /> Add Quote Template
          </IntentButton>
        }
      >
        <DataGrid
          rows={visibleTemplates}
          columns={cols}
          rowKey={(t) => t.quoteTemplateId}
          totalRows={visibleTemplates.length}
          pageIndex={0}
          pageSize={visibleTemplates.length || 1}
          loading={isLoading}
          emptyHeadline="No quote templates found."
          onPageChange={() => void 0}
        />
      </ListPage>

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) closeDrawer();
        }}
        title={editing ? "Edit Template" : "New Template"}
      >
        <div className="space-y-4 p-4">
          <div>
            <Label htmlFor="qt-name">Name *</Label>
            <Input
              id="qt-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Template name"
            />
          </div>
          <div>
            <Label htmlFor="qt-description">Description</Label>
            <Input
              id="qt-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <div>
            <Label htmlFor="qt-body">Body text</Label>
            <textarea
              id="qt-body"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              rows={8}
              value={form.bodyText}
              onChange={(e) => setForm((f) => ({ ...f, bodyText: e.target.value }))}
              placeholder="Template body content…"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="qt-default"
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
            />
            <Label htmlFor="qt-default">Set as default template</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={closeDrawer}>
              Cancel
            </Button>
          </div>
        </div>
      </DetailModal>
    </>
  );
}
